<template>
  <Offcanvas name="adminEditOffcanvas">
    <template v-slot:header>
      <h4>管理者の編集</h4>
    </template>
    <template v-slot:default>
      <form @submit.prevent="update" v-if="editingAdmin">
        <AdminForm v-model="editingAdmin" :is-edit="true" :current-admin-id="currentAdmin?.id" :current-admin-permissions="currentAdmin?.permissions || []"/>
        <div class="mt-3">
          <button class="btn btn-primary w-100">更新</button>
        </div>
      </form>
    </template>
    <template v-slot:footer>
      <div class="text-end" v-if="editingAdmin && currentAdmin">
        <button
          v-if="editingAdmin.id !== currentAdmin.id && hasManagePermission"
          class="btn btn-danger"
          @click="del"
        >
          削除
        </button>
      </div>
    </template>
  </Offcanvas>
</template>

<script setup>
import {computed, onMounted, ref, watch} from 'vue';
import {useRouter} from 'vue-router';
import {deleteAdmin, updateAdmin} from '../../../api/admin/AdminApi';
import {getCurrentAdmin} from '../../../api/admin/AuthApi';
import Offcanvas from '../../../components/Offcanvas.vue';
import useFlashMessage from '../../../composables/useFlashMessage';
import useOffcanvas from '../../../composables/useOffcanvas';
import Admin from '../../../models/Admin';
import AdminForm from './AdminForm.vue';

const props = defineProps({
  admin: Admin,
});

const emit = defineEmits(['update']);

const router = useRouter();
const {successMessage, warningMessage, errorMessage} = useFlashMessage();
const {closeOffcanvas} = useOffcanvas('adminEditOffcanvas');

const editingAdmin = ref();
const currentAdmin = ref();

// Check if current admin has MANAGE permission
const hasManagePermission = computed(() => {
  return currentAdmin.value?.permissions?.some(p => p.permissionType === 'MANAGE') || false;
});

onMounted(async () => {
  currentAdmin.value = await getCurrentAdmin();
});

watch(props, () => editingAdmin.value = new Admin({...props.admin}));

/**
 * Update Admin Account
 *
 * @returns {Promise<void>}
 */
const update = async () => {
  // Check if current admin still has MANAGE permission
  const freshCurrentAdmin = await getCurrentAdmin();
  const hasManagePermission = freshCurrentAdmin.permissions?.some(p => p.permissionType === 'MANAGE');

  if (!hasManagePermission) {
    errorMessage('管理権限がないためデータを編集できません');
    return;
  }

  try {
    await updateAdmin(editingAdmin.value);
    successMessage('管理者「' + editingAdmin.value.name + '」を更新しました。');
    emit('update');
    closeOffcanvas();
  } catch (e) {
    errorMessage(e.message);
  }
};

/**
 * Delete Admin Account
 *
 * @returns {Promise<void>}
 */
const del = async () => {
  // Check if current admin has MANAGE permission
  const freshCurrentAdmin = await getCurrentAdmin();
  const hasManagePermission = freshCurrentAdmin.permissions?.some(p => p.permissionType === 'MANAGE');

  if (!hasManagePermission) {
    errorMessage('管理権限がないため削除できません');
    return;
  }

  if (confirm('本当に削除してもよろしいですか？')) {
    try {
      await deleteAdmin(editingAdmin.value);
      warningMessage('管理者「' + editingAdmin.value.name + '」を削除しました。');
      await router.push({name: 'admin.admins.index'});
      closeOffcanvas();
    } catch (e) {
      if (e.status === 403) {
        errorMessage('削除権限がありません');
      } else {
        errorMessage(e.message);
      }
    }
  }
};
</script>
